/**
 * ActionCard Component
 *
 * Container with header showing title, total count, and delta.
 *
 * Atomic level: Organism
 * Composes: DeltaIndicator, Card (shadcn)
 * Used by: ActionCardsSection
 */

import React from 'react';
import { Card, CardContent } from '../../../ui/card';
import { DeltaIndicator } from './DeltaIndicator';
import type { ActionCardProps } from '../types/today.types';

/**
 * Action card container with header showing title, total count, and delta.
 *
 * @example
 * ```tsx
 * <ActionCard
 *   title="Eligible Complaints"
 *   total={12}
 *   delta={3}
 *   trend="up"
 * >
 *   {records.map(r => <RecordRow key={r.id} record={r} />)}
 * </ActionCard>
 * ```
 */
export function ActionCard({
  title,
  total,
  delta,
  trend: _trend,
  children,
}: ActionCardProps): React.ReactElement {
  return (
    <Card className="h-full gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
      <div className="flex items-start justify-between gap-4 border-b border-border/70 px-5 py-4">
        <div className="space-y-1">
          <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.02em] text-foreground">
            {title}
          </h3>
          <p className="text-[12px] text-muted-foreground">Last 24 hours</p>
        </div>
        <div className="text-right">
          <div className="font-jakarta text-3xl font-bold tracking-[-0.04em] text-foreground">
            {total}
          </div>
          <DeltaIndicator delta={delta} showValue size="sm" />
        </div>
      </div>

      <CardContent className="p-0">
        <div className="divide-y divide-border/60">{children}</div>
      </CardContent>
    </Card>
  );
}
